import { useEffect } from 'react';

import { useHashScroll } from '@sureapp/wl-toggle-common/dist/hooks/useHashScroll';
import { analytics } from '@sureapp/wl-toggle-common/dist/utils';
import kebabCase from 'lodash/kebabCase';
import lowerCase from 'lodash/lowerCase';
import ReactHtmlParser, { convertNodeToElement } from 'react-html-parser';

import CmsContent, { TRenderCmsContent } from '@@/auto/components/CmsContent';
import personalInformation from '@@/auto/constants/privacyPolicyPersonalInformationV2';
import { ICmsField, TConfig } from '@@/auto/interfaces';
import LayoutSelfService from '@@/common/components/LayoutSelfService';
import { useCmsContent } from '@@/common/hooks/useCmsContent';

import SpinnerView from '../SpinnerView';

import classes from './PrivacyPolicyView.module.css';

type TCreateCmsRenderer = (fields: string[]) => TRenderCmsContent;

const createCmsContentRendererForFields: TCreateCmsRenderer = (fieldNames) => ({
  // eslint-disable-next-line react/prop-types
  cmsContent,
}) => {
  const sections: ICmsField[] = [];

  fieldNames.forEach((fieldName) => {
    const field = cmsContent?.fields.find(
      (cmsField) => cmsField.field_template.name === fieldName
    );

    if (field) {
      sections.push(field);
    }
  });

  return (
    <>
      {sections?.map((htmlSection) => {
        const fieldName = kebabCase(htmlSection.field_template.name);
        return (
          <div
            id={fieldName}
            key={htmlSection?.id}
            className={classes[fieldName]}
          >
            {ReactHtmlParser(htmlSection?.text || '', {
              transform: function transformAnchorTags(node, index) {
                if (node.type === 'tag' && node.name === 'h2') {
                  return (
                    <a
                      className={classes.sectionHeading}
                      href={`#${fieldName}`}
                    >
                      {convertNodeToElement(node, index, transformAnchorTags)}
                    </a>
                  );
                }
                return convertNodeToElement(node, index, transformAnchorTags);
              },
            })}
          </div>
        );
      })}
    </>
  );
};

export type TPrivacyPolicyView = {
  supportEmail: TConfig['supportEmail'];
  privacyPolicySlug: string;
  insuranceUrl?: string;
  showPersonalInfoTable?: boolean;
  showDoNotSellMyPersonalInfo?: boolean;
  showSharingPersonalInformation?: boolean;
  showYourChoices?: boolean;
  firstBlockCmsFields?: string[];
  secondBlockCmsFields?: string[];
  thirdBlockCmsFields?: string[];
};

const cmsModelSlug = 'privacy-policy-v2';

function PrivacyPolicyView({
  privacyPolicySlug,
  showPersonalInfoTable,
}: TPrivacyPolicyView) {
  const cmsContent = useCmsContent(cmsModelSlug);

  useEffect(() => {
    analytics.page();
  }, []);

  useHashScroll();

  if (!cmsContent) {
    return <SpinnerView />;
  }

  return (
    <LayoutSelfService
      className={classes.root}
      header="Privacy Policy"
      backgroundImgSrc={null}
    >
      <CmsContent
        data-testid="privacyPolicy-introduction-content"
        modelSlug={cmsModelSlug}
        entrySlug={privacyPolicySlug}
        renderContent={createCmsContentRendererForFields(['Before Table'])}
      />

      {showPersonalInfoTable && (
        <table className={classes.personalInfoTable}>
          <thead>
            <tr>
              <td>Category</td>
              <td>Examples</td>
            </tr>
          </thead>
          <tbody>
            {personalInformation.map(({ category, examples }) => {
              const key = lowerCase(category);
              return (
                <tr key={key} data-testid={`PersonalInformationTable_${key}`}>
                  <td>{category}</td>
                  <td>{examples}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <CmsContent
        data-testid="privacyPolicy-middle-content"
        modelSlug={cmsModelSlug}
        entrySlug={privacyPolicySlug}
        renderContent={createCmsContentRendererForFields(['After Table'])}
      />
    </LayoutSelfService>
  );
}

export default PrivacyPolicyView;
